import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {showErrorToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {
  formatAnswer,
  fullName,
  TRACKING_STATUS_BADGE,
  trackingStatusLabel,
} from '../application/ApplicationModel'
import {formatDateTime} from '../shared/DocumentApi'
import {CvActions} from '../shared/CvActions'
import {TagChips} from '../tag/TagChips'
import {loadMailLogs} from '../mail-template/Api'
import {
  IMailLogModel,
  MAIL_STATUS_BADGE,
  MAIL_STATUS_LABEL,
  MAIL_TRIGGER_LABEL,
} from '../mail-template/MailLogModel'
import {getTracking} from './Api'
import {HISTORY_ACTION_LABEL, ITrackingModel} from './TrackingModel'

type Props = {
  trackingId: string | null
  onClose: () => void
}

/** Aday takibinin detayı: aday, süreçteki konumu, adım geçmişi ve başvuru cevapları */
export const TrackingDetailModal: FC<Props> = ({trackingId, onClose}) => {
  const [tracking, setTracking] = useState<ITrackingModel | null>(null)
  const [mailLogs, setMailLogs] = useState<IMailLogModel[]>([])

  useEffect(() => {
    if (!trackingId) return
    setTracking(null)
    setMailLogs([])
    getTracking(trackingId)
      .then(({data}) => setTracking(data.payload))
      .catch((error) => showErrorToast(apiErrorMessage(error, 'Kayıt yüklenemedi')))
  }, [trackingId])

  // Otomatik mail kayıtları başvuru üzerinden okunur
  useEffect(() => {
    const applyJobId = tracking?.applyJobId
    if (!applyJobId) return
    loadMailLogs(applyJobId)
      .then(({data}) => setMailLogs(data.payload || []))
      .catch(() => setMailLogs([]))
  }, [tracking?.applyJobId])

  return (
    <Modal show={!!trackingId} onHide={onClose} size='lg' dialogClassName='modal-dialog-centered'>
      <div className='modal-header'>
        <h5 className='modal-title'>Aday Takip Detayı</h5>
        <button type='button' className='btn-close' onClick={onClose} />
      </div>
      <div className='modal-body'>
        {!tracking ? (
          <div className='d-flex justify-content-center py-10'>
            <span className='spinner-border text-primary' />
          </div>
        ) : (
          <>
            <div className='d-flex flex-wrap justify-content-between gap-3 mb-6'>
              <div>
                <div className='fs-3 fw-bolder text-gray-900'>{fullName(tracking)}</div>
                <div className='text-muted'>
                  {tracking.email}
                  {tracking.phone ? ` · ${tracking.phone}` : ''}
                </div>
                <TagChips tags={tracking.tags} />
              </div>
              <div className='align-self-start'>
                <CvActions documentId={tracking.documentId} fileName={`${fullName(tracking)} CV`} />
              </div>
            </div>

            <div className='row g-4 mb-8'>
              <div className='col-md-4'>
                <div className='text-muted fs-7'>İlan</div>
                <div className='fw-semibold'>{tracking.jobTitle}</div>
              </div>
              <div className='col-md-4'>
                <div className='text-muted fs-7'>Süreç</div>
                <div className='fw-semibold'>{tracking.hrProcessName}</div>
              </div>
              <div className='col-md-4'>
                <div className='text-muted fs-7'>Bulunduğu adım</div>
                <div className='fw-semibold'>
                  {tracking.currentStepName}{' '}
                  <span className={`badge ${TRACKING_STATUS_BADGE[tracking.status]} ms-1`}>
                    {trackingStatusLabel(tracking.status)}
                  </span>
                </div>
              </div>
              {tracking.assignedUserName && (
                <div className='col-md-4'>
                  <div className='text-muted fs-7'>Arayan kullanıcı</div>
                  <div className='fw-semibold'>{tracking.assignedUserName}</div>
                </div>
              )}
              {tracking.salaryExpectation && (
                <div className='col-md-4'>
                  <div className='text-muted fs-7'>Ücret beklentisi</div>
                  <div className='fw-semibold'>{tracking.salaryExpectation}</div>
                </div>
              )}
              {tracking.note && (
                <div className='col-12'>
                  <div className='text-muted fs-7'>Not</div>
                  <div style={{whiteSpace: 'pre-line'}}>{tracking.note}</div>
                </div>
              )}
            </div>

            <div className='fs-5 fw-bolder text-gray-800 mb-4'>Süreç Geçmişi</div>
            <div className='timeline-label mb-8'>
              {(tracking.history || []).map((history, index) => (
                <div key={index} className='timeline-item'>
                  <div className='timeline-label fw-semibold text-gray-600 fs-8' style={{width: 110}}>
                    {formatDateTime(history.createdDate)}
                  </div>
                  <div className='timeline-badge'>
                    <i className='fa fa-genderless text-primary fs-1'></i>
                  </div>
                  <div className='timeline-content ps-3'>
                    <span className='fw-bold text-gray-800'>{HISTORY_ACTION_LABEL[history.action]}</span>
                    <span className='text-muted ms-2'>
                      {history.action === 'START' && history.toStepName}
                      {(history.action === 'ADVANCE' || history.action === 'BACK') &&
                        `${history.fromStepName} → ${history.toStepName}`}
                      {history.action === 'STATUS' && trackingStatusLabel(history.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className='fs-5 fw-bolder text-gray-800 mb-3'>Gönderilen Otomatik Mailler</div>
            {mailLogs.length === 0 ? (
              <div className='text-muted mb-8'>Bu adaya otomatik mail gönderilmedi.</div>
            ) : (
              <div className='table-responsive mb-8'>
                <table className='table table-row-dashed align-middle gy-2'>
                  <thead>
                    <tr className='fw-bold text-muted fs-8'>
                      <th>Tarih</th>
                      <th>Konu</th>
                      <th>Kural</th>
                      <th>Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mailLogs.map((mail) => (
                      <tr key={mail.id}>
                        <td className='text-muted fs-8' style={{width: 110}}>
                          {formatDateTime(mail.createdDate)}
                        </td>
                        <td>
                          <div className='fw-semibold fs-7'>{mail.subject}</div>
                          {mail.mailTemplateName && (
                            <div className='text-muted fs-8'>{mail.mailTemplateName}</div>
                          )}
                        </td>
                        <td className='fs-8'>
                          {MAIL_TRIGGER_LABEL[mail.triggerType]}
                          {mail.triggerReference ? `: ${mail.triggerReference}` : ''}
                        </td>
                        <td style={{width: 140}}>
                          <span className={`badge ${MAIL_STATUS_BADGE[mail.status]}`}>
                            {MAIL_STATUS_LABEL[mail.status]}
                          </span>
                          {mail.errorMessage && (
                            <div className='text-muted fs-8'>{mail.errorMessage}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className='fs-5 fw-bolder text-gray-800 mb-3'>Başvuru Cevapları</div>
            {(tracking.answers || []).length === 0 ? (
              <div className='text-muted'>Cevaplanmış soru yok.</div>
            ) : (
              (tracking.answers || []).map((answer) => (
                <div key={answer.questionId} className='d-flex py-2 border-bottom border-gray-200'>
                  <span className='text-muted fw-semibold' style={{width: 220, flexShrink: 0}}>
                    {answer.questionLabel}
                  </span>
                  <span>{formatAnswer(answer.value)}</span>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
